/**
 * EditPanel
 *
 * Right-side panel for editing the selected section's elements.
 * Shows text inputs, image thumbnails, and CTA editors.
 * Phase 7: Adds section-level controls (move, remove, add).
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { applyStructuralChanges } from '../../api/ai-editor';
import type { StructuralChange } from '../../api/ai-editor';
import type { EditorMode } from '../types';
import { ImageEditModal } from './ImageEditModal';

// =============================================================================
// TYPES
// =============================================================================

/** Simplified element for the edit panel */
export interface EditableElement {
  id: string;
  type: 'text' | 'image' | 'cta';
  label: string;
  value: string;
  /** For images: alt text */
  placeholder?: string;
  /**
   * For images: CSS `object-position` as "X% Y%".
   *
   * Absent means the browser default "50% 50%". Mirrors
   * EditableElement.position in the orchestrator.
   */
  position?: string;
  /** For buttons: the link target. Separate from `value`, which is the label. */
  href?: string;
  constraints?: {
    maxLength?: number;
    allowHtml?: boolean;
    allowLineBreaks?: boolean;
    imageTypes?: string[];
  };
}

interface EditPanelProps {
  /** Section ID */
  sectionId: string;
  /** Section type label (e.g., "Hero Section") */
  sectionLabel: string;
  /** Section type key */
  sectionType: string;
  /** Elements to edit */
  elements: EditableElement[];
  /** Website ID for structural changes */
  websiteId?: string;
  /** Current page slug */
  activePage?: string;
  /** Index of this section in the page */
  sectionIndex?: number;
  /** Total sections on the page */
  totalSections?: number;
  /** All section IDs on the current page (for reordering) */
  pageSectionIds?: string[];
  /** Editor mode */
  mode?: EditorMode;
  /**
   * Whether photo management (add / remove / reorder) is offered on data-model
   * galleries. The portal passes the customer's gallery_editing flag; the
   * server enforces the same gate, so this only decides what is SHOWN.
   */
  allowGalleryPhotos?: boolean;
  /** Called when an element value changes */
  onChange: (elementId: string, newValue: string) => void;
  /**
   * Called when an image's framing changes.
   *
   * Optional because framing needs a save path that writes the suffixed
   * element key. Where it is absent the control stays hidden rather than
   * letting someone reframe an image and lose it silently on save.
   */
  onPositionChange?: (elementId: string, position: string) => void;
  /**
   * Called when a button's link target changes.
   *
   * Optional for the same reason as onPositionChange: the change rides a
   * suffixed element key, which only a caller wired to the staging element API
   * can send. Absent, the field is hidden rather than accepting an edit it
   * cannot store.
   */
  onHrefChange?: (elementId: string, href: string) => void;
  /** Called when panel is closed */
  onClose: () => void;
  /** Called after structural changes (add/remove/reorder) */
  onContentChange?: () => void;
}

// =============================================================================
// SECTION TYPE LABELS
// =============================================================================

const SECTION_LABELS: Record<string, string> = {
  hero: 'Hero',
  services: 'Services',
  about: 'About',
  testimonials: 'Testimonials',
  gallery: 'Gallery',
  stats: 'Stats',
  credentials: 'Credentials',
  process: 'Process',
  team: 'Team',
  cta: 'Call to Action',
  faq: 'FAQ',
  location: 'Location',
  contact: 'Contact',
};

const ADDABLE_SECTION_TYPES = [
  { type: 'services', label: 'Services' },
  { type: 'about', label: 'About' },
  { type: 'testimonials', label: 'Testimonials' },
  { type: 'gallery', label: 'Gallery' },
  { type: 'stats', label: 'Stats' },
  { type: 'credentials', label: 'Credentials' },
  { type: 'process', label: 'Process' },
  { type: 'team', label: 'Team' },
  { type: 'cta', label: 'Call to Action' },
  { type: 'faq', label: 'FAQ' },
  { type: 'location', label: 'Location' },
  { type: 'contact', label: 'Contact' },
];

// =============================================================================
// ELEMENT EDITORS
// =============================================================================

function TextEditor({
  element,
  onChange,
}: {
  element: EditableElement;
  onChange: (value: string) => void;
}) {
  const [value, setValue] = useState(element.value);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const focusedRef = useRef(false);

  // Sync with external value changes — but never while the field has focus. A
  // reload that lands between two keystrokes would otherwise replace what is
  // being typed with the last saved value, which reads as the field resetting.
  useEffect(() => {
    if (!focusedRef.current) setValue(element.value);
  }, [element.value]);

  const handleChange = useCallback(
    (newValue: string) => {
      setValue(newValue);
      // Debounced callback
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        onChange(newValue);
      }, 700);
    },
    [onChange]
  );

  // Leaving the field flushes any pending save at once, so a short pause is
  // never the only thing standing between an edit and being saved.
  const handleBlur = useCallback(() => {
    focusedRef.current = false;
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = undefined;
      onChange(valueRef.current);
    }
    setValue(element.value);
  }, [onChange, element.value]);
  const valueRef = useRef(value);
  valueRef.current = value;

  const maxLength = element.constraints?.maxLength;
  // `allowLineBreaks` is stamped at section creation, so elements created
  // before it existed never carry it — and a multi-line value shown in a
  // single-line <input> displays with its line breaks silently flattened
  // ("CROSSFIT PTC100 Stoudenmire Rd..."), which reads as data loss even
  // though the stored value and the page are correct. A value that already
  // holds a newline is proof enough that this field takes them.
  const isLong =
    element.constraints?.allowLineBreaks ||
    (maxLength && maxLength > 120) ||
    (typeof value === 'string' && value.includes('\n'));

  // Grow to fit rather than scrolling inside a fixed three-row box. A venue's
  // address is three lines and a details paragraph can be several; hiding the
  // rest behind a scrollbar is how you edit the wrong half of a value.
  const grow = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 400)}px`;
  };
  const growRef = useCallback((el: HTMLTextAreaElement | null) => grow(el), []);

  return (
    <div className="space-y-1">
      <label className="block text-xs font-medium text-gray-500">
        {element.label}
      </label>
      {isLong ? (
        <textarea
          ref={growRef}
          value={value}
          onChange={(e) => {
            grow(e.currentTarget);
            handleChange(e.target.value);
          }}
          maxLength={maxLength}
          onFocus={() => { focusedRef.current = true; }}
          onBlur={handleBlur}
          rows={2}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y overflow-hidden"
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          maxLength={maxLength}
          onFocus={() => { focusedRef.current = true; }}
          onBlur={handleBlur}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      )}
      {maxLength && (
        <div className="text-xs text-gray-400 text-right">
          {value.length}/{maxLength}
        </div>
      )}
    </div>
  );
}

function ImageEditor({
  element,
  onChange,
  onPositionChange,
  websiteId,
  text,
}: {
  element: EditableElement;
  onChange: (value: string) => void;
  onPositionChange?: (position: string) => void;
  websiteId?: string;
  /**
   * The slot's alt and caption, when they live in their OWN elements.
   *
   * A converted gallery keeps them as `images.N.alt` and `images.N.caption`
   * beside the image, and the panel renders each as its own field. Without
   * this the modal opened blank and discarded whatever was typed into it —
   * two fields showing alt text, one of them a no-op.
   */
  text?: {
    alt?: { value: string; onChange: (v: string) => void };
    caption?: { value: string; onChange: (v: string) => void };
  };
}) {
  const [showModal, setShowModal] = useState(false);

  const handleSave = useCallback(
    (newUrl: string, altText: string, title: string, position: string) => {
      onChange(newUrl);
      // Framing goes to its own handler: it is a separate staged change, and
      // silently dropping it here is what the enableFraming gate exists to
      // prevent. Only sent when a handler is wired, matching that gate.
      if (onPositionChange && position) onPositionChange(position);

      // Only written where the slot actually stores them. Elsewhere the modal's
      // alt lives on the image element itself and there is nothing to sync.
      if (text?.alt && altText !== text.alt.value) text.alt.onChange(altText);
      if (text?.caption && title !== text.caption.value) text.caption.onChange(title);

      setShowModal(false);
    },
    [onChange, onPositionChange, text]
  );

  return (
    <div className="space-y-2">
      <label className="block text-xs font-medium text-gray-500">
        {element.label}
      </label>
      <div className="relative rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
        {element.value ? (
          <img
            src={element.value as string}
            alt={element.placeholder || element.label}
            className="w-full h-32 object-cover"
            /* The thumbnail is itself an object-cover crop, so without this it
               shows a centre crop of an image the page frames differently —
               the panel and the canvas disagreeing about the same picture. */
            style={{ objectPosition: element.position }}
            onError={(e) => {
              (e.target as HTMLImageElement).src =
                'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="128" fill="%23e5e7eb"><rect width="200" height="128"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%239ca3af" font-size="14">No image</text></svg>';
            }}
          />
        ) : (
          <div className="w-full h-32 flex flex-col items-center justify-center text-gray-400 gap-1">
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-xs">No image set</span>
          </div>
        )}
        <button
          onClick={() => setShowModal(true)}
          className="absolute bottom-2 right-2 px-2.5 py-1 text-xs font-medium bg-white/90 hover:bg-white border border-gray-300 rounded-md shadow-sm transition-colors"
        >
          {element.value ? 'Change Image' : 'Add Image'}
        </button>
      </div>

      {showModal && websiteId && (
        <ImageEditModal
          currentValue={element.value as string || ''}
          label={element.label}
          currentAlt={text?.alt ? text.alt.value : element.placeholder}
          currentTitle={text?.caption?.value}
          titleLabel={text?.caption ? 'Caption' : 'Title'}
          titleHint={text?.caption ? '(shown on the image)' : '(tooltip)'}
          currentPosition={element.position}
          enableFraming={!!onPositionChange}
          websiteId={websiteId}
          onSave={handleSave}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}

function CtaEditor({
  element,
  onChange,
  onHrefChange,
}: {
  element: EditableElement;
  onChange: (value: string) => void;
  onHrefChange?: (href: string) => void;
}) {
  const [value, setValue] = useState(element.value);
  const [href, setHref] = useState(element.href ?? '');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const hrefDebounceRef = useRef<ReturnType<typeof setTimeout>>();
  const focusedRef = useRef<'label' | 'href' | null>(null);

  // Never replace a field while it has focus: a reload that lands between two
  // keystrokes put the last saved value back, which reads as the field resetting.
  useEffect(() => {
    if (focusedRef.current !== 'label') setValue(element.value);
  }, [element.value]);

  useEffect(() => {
    if (focusedRef.current !== 'href') setHref(element.href ?? '');
  }, [element.href]);

  const valueRef = useRef(value);
  valueRef.current = value;
  const hrefRef = useRef(href);
  hrefRef.current = href;

  const handleChange = useCallback(
    (newValue: string) => {
      setValue(newValue);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        debounceRef.current = undefined;
        onChange(newValue);
      }, 700);
    },
    [onChange]
  );

  const handleHrefChange = useCallback(
    (newHref: string) => {
      setHref(newHref);
      if (!onHrefChange) return;
      if (hrefDebounceRef.current) clearTimeout(hrefDebounceRef.current);
      // A longer pause than the label's: a link is typed in one go and every
      // partial value ("tel:4", "tel:47") would otherwise be saved as it passed.
      hrefDebounceRef.current = setTimeout(() => {
        hrefDebounceRef.current = undefined;
        // Blank would erase the target and leave a button that goes nowhere;
        // the server refuses it too, so don't send it.
        if (newHref.trim()) onHrefChange(newHref.trim());
      }, 900);
    },
    [onHrefChange]
  );

  // Leaving a field saves whatever is pending at once.
  const flushLabel = useCallback(() => {
    focusedRef.current = null;
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = undefined;
      onChange(valueRef.current);
    }
  }, [onChange]);
  const flushHref = useCallback(() => {
    focusedRef.current = null;
    if (hrefDebounceRef.current) {
      clearTimeout(hrefDebounceRef.current);
      hrefDebounceRef.current = undefined;
      if (onHrefChange && hrefRef.current.trim()) onHrefChange(hrefRef.current.trim());
    }
  }, [onHrefChange]);

  return (
    <div className="space-y-1">
      <label className="block text-xs font-medium text-gray-500">
        {element.label}
      </label>
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          maxLength={element.constraints?.maxLength || 50}
          onFocus={() => { focusedRef.current = 'label'; }}
          onBlur={flushLabel}
          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <div className="w-2 h-2 rounded-full bg-blue-500 shrink-0" title="Button" />
      </div>
      {onHrefChange && (
        <div className="flex items-center gap-2 pt-0.5">
          <span className="text-xs text-gray-400 w-10 shrink-0">Links to</span>
          <input
            type="text"
            value={href}
            onChange={(e) => handleHrefChange(e.target.value)}
            placeholder="/contact/ or https://…"
            onFocus={() => { focusedRef.current = 'href'; }}
            onBlur={flushHref}
            spellCheck={false}
            className="flex-1 px-3 py-1.5 text-xs font-mono border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      )}
    </div>
  );
}

// =============================================================================
// SECTION ACTION BUTTONS
// =============================================================================

function SectionActions({
  websiteId,
  activePage,
  sectionId,
  sectionType,
  sectionIndex,
  totalSections,
  pageSectionIds,
  onContentChange,
}: {
  websiteId: string;
  activePage: string;
  sectionId: string;
  sectionType: string;
  sectionIndex: number;
  totalSections: number;
  pageSectionIds: string[];
  onContentChange: () => void;
}) {
  const [isActing, setIsActing] = useState(false);
  const [showAddPicker, setShowAddPicker] = useState(false);
  const [showConfirmRemove, setShowConfirmRemove] = useState(false);
  const [error, setError] = useState('');

  const isHero = sectionType === 'hero' && sectionIndex === 0;
  const canMoveUp = sectionIndex > (isHero ? 1 : 0) && !isHero;
  // For non-hero sections at index 1 when hero is at 0, can still move up only if not hero
  const canMoveUpActual = !isHero && sectionIndex > 0 && !(sectionIndex === 1 && totalSections > 1);
  const canMoveDown = !isHero && sectionIndex < totalSections - 1;
  const canRemove = !isHero && totalSections > 1;

  const handleAction = useCallback(async (changes: StructuralChange[]) => {
    if (isActing) return;
    setIsActing(true);
    setError('');
    try {
      const result = await applyStructuralChanges(websiteId, changes);
      if (result.success) {
        onContentChange();
      } else {
        setError(result.error || 'Failed to apply change');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to apply change');
    } finally {
      setIsActing(false);
      setShowConfirmRemove(false);
      setShowAddPicker(false);
    }
  }, [websiteId, isActing, onContentChange]);

  const handleMoveUp = useCallback(() => {
    if (!canMoveUp && !canMoveUpActual) return;
    // Build new order using section IDs (swap current with previous)
    const newOrder = [...pageSectionIds];
    [newOrder[sectionIndex], newOrder[sectionIndex - 1]] = [newOrder[sectionIndex - 1], newOrder[sectionIndex]];
    handleAction([{
      id: crypto.randomUUID(),
      action: 'reorder_sections',
      target: { page: activePage },
      data: { order: newOrder },
    }]);
  }, [canMoveUp, canMoveUpActual, sectionIndex, pageSectionIds, activePage, handleAction]);

  const handleMoveDown = useCallback(() => {
    if (!canMoveDown) return;
    const newOrder = [...pageSectionIds];
    [newOrder[sectionIndex], newOrder[sectionIndex + 1]] = [newOrder[sectionIndex + 1], newOrder[sectionIndex]];
    handleAction([{
      id: crypto.randomUUID(),
      action: 'reorder_sections',
      target: { page: activePage },
      data: { order: newOrder },
    }]);
  }, [canMoveDown, sectionIndex, pageSectionIds, activePage, handleAction]);

  const handleRemove = useCallback(() => {
    handleAction([{
      id: crypto.randomUUID(),
      action: 'remove_section',
      target: { page: activePage, section: sectionId },
      data: {},
    }]);
  }, [sectionId, activePage, handleAction]);

  const handleAdd = useCallback((type: string) => {
    handleAction([{
      id: crypto.randomUUID(),
      action: 'add_section',
      target: { page: activePage },
      data: { type },
      position: sectionIndex + 1,
    }]);
  }, [sectionIndex, activePage, handleAction]);

  return (
    <div className="relative">
      <div className="flex items-center gap-1">
        {/* Move Up */}
        <button
          onClick={handleMoveUp}
          disabled={isActing || (!canMoveUp && !canMoveUpActual)}
          className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed rounded transition-colors"
          title="Move section up"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        </button>
        {/* Move Down */}
        <button
          onClick={handleMoveDown}
          disabled={isActing || !canMoveDown}
          className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed rounded transition-colors"
          title="Move section down"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {/* Add Section */}
        <button
          onClick={() => setShowAddPicker(!showAddPicker)}
          disabled={isActing || totalSections >= 8}
          className="p-1 text-gray-400 hover:text-green-600 disabled:opacity-30 disabled:cursor-not-allowed rounded transition-colors"
          title="Add section below"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </button>
        {/* Remove Section */}
        <button
          onClick={() => setShowConfirmRemove(true)}
          disabled={isActing || !canRemove}
          className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed rounded transition-colors"
          title="Remove section"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="absolute top-full right-0 mt-1 px-2 py-1 bg-red-50 text-red-600 text-xs rounded-md shadow-sm whitespace-nowrap z-10">
          {error}
        </div>
      )}

      {/* Add Section Picker */}
      {showAddPicker && (
        <div className="absolute top-full right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1 max-h-64 overflow-y-auto">
          <div className="px-3 py-1.5 text-xs font-medium text-gray-400 uppercase tracking-wide">
            Add Section
          </div>
          {ADDABLE_SECTION_TYPES.map((st) => (
            <button
              key={st.type}
              onClick={() => handleAdd(st.type)}
              className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
            >
              {st.label}
            </button>
          ))}
          <div className="border-t border-gray-100 mt-1 pt-1">
            <button
              onClick={() => setShowAddPicker(false)}
              className="w-full text-left px-3 py-1.5 text-xs text-gray-400 hover:text-gray-600"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Remove Confirmation */}
      {showConfirmRemove && (
        <div className="absolute top-full right-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-20 p-3">
          <p className="text-sm text-gray-700 mb-2">
            Remove this section?
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setShowConfirmRemove(false)}
              className="flex-1 px-2 py-1 text-xs text-gray-600 border border-gray-200 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleRemove}
              disabled={isActing}
              className="flex-1 px-2 py-1 text-xs text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
            >
              {isActing ? 'Removing...' : 'Remove'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// EDIT PANEL COMPONENT
// =============================================================================

// =============================================================================
// GALLERY PHOTOS
// =============================================================================

/** Element ids for a data-model gallery image: `{sectionId}_images_{n}_url`. */
const GALLERY_IMAGE_ID = /_images_(\d+)_url$/;

/**
 * Is this a gallery that has been converted to the data model?
 *
 * A generated gallery's elements carry `sections.gallery.images[0]`-derived ids
 * and cannot gain or lose a photo — the section has to be converted first
 * (ADR-009). Offering Add/Remove on a frozen gallery would produce changes the
 * renderer never applies, so the controls appear only once the ids show the
 * data-model shape.
 */
function galleryImageIndices(sectionType: string, elements: EditableElement[]): number[] {
  if (sectionType !== 'gallery') return [];
  return elements
    .map((el) => el.id.match(GALLERY_IMAGE_ID))
    .filter((m): m is RegExpMatchArray => Boolean(m))
    .map((m) => Number(m[1]))
    .sort((a, b) => a - b);
}

/** The one add-photo call both the header and the bottom button share. */
async function addGalleryPhoto(
  websiteId: string,
  activePage: string,
  sectionId: string,
  photoCount: number
): Promise<void> {
  const result = await applyStructuralChanges(websiteId, [
    {
      id: crypto.randomUUID(),
      action: 'add_gallery_image',
      target: { page: activePage, section: sectionId },
      data: { url: '', alt: `Gallery image ${photoCount + 1}` },
    },
  ]);
  if (!result?.success) throw new Error(result?.error || 'Could not add the photo.');
}

/**
 * Move a photo one slot up or down.
 *
 * One server-side `swap_gallery_images` per click: the two slot groups
 * transpose ids so url, alt, caption and framing travel together, and the
 * emitted renames keep historical staged deltas attached to their image —
 * a value swap here would be silently undone at publish, because deltas are
 * re-applied by element id.
 */
function MovePhotoButton({
  websiteId,
  activePage,
  sectionId,
  index,
  direction,
  disabled,
  onContentChange,
}: {
  websiteId: string;
  activePage: string;
  sectionId: string;
  index: number;
  direction: -1 | 1;
  disabled: boolean;
  onContentChange: () => void;
}) {
  const [busy, setBusy] = useState(false);

  const handleMove = useCallback(async () => {
    setBusy(true);
    try {
      const result = await applyStructuralChanges(websiteId, [
        {
          id: crypto.randomUUID(),
          action: 'swap_gallery_images',
          target: { page: activePage, section: sectionId, index },
          data: { withIndex: index + direction },
        },
      ]);
      if (!result?.success) throw new Error(result?.error || 'Could not move the photo.');
      onContentChange();
    } catch (err) {
      window.alert((err as Error).message);
      setBusy(false);
    }
  }, [websiteId, activePage, sectionId, index, direction, onContentChange]);

  return (
    <button
      onClick={handleMove}
      disabled={busy || disabled}
      title={direction === -1 ? 'Move up' : 'Move down'}
      className="shrink-0 w-6 h-6 flex items-center justify-center rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-400"
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={direction === -1 ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'} />
      </svg>
    </button>
  );
}

interface GalleryPhotoControlsProps {
  websiteId: string;
  activePage: string;
  sectionId: string;
  photoCount: number;
  onContentChange: () => void;
}

/**
 * Add and remove photos on a converted gallery.
 *
 * A new photo is added as an EMPTY slot rather than opening a picker here. The
 * slot renders as "Click to add image" and appears in the list below as a
 * normal image element, so choosing the picture reuses the existing image
 * editor — including its framing control — instead of a second, parallel flow.
 */
function GalleryPhotoControls({
  websiteId,
  activePage,
  sectionId,
  photoCount,
  onContentChange,
}: GalleryPhotoControlsProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAdd = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await addGalleryPhoto(websiteId, activePage, sectionId, photoCount);
      onContentChange();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }, [websiteId, activePage, sectionId, photoCount, onContentChange]);

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <p className="text-xs font-semibold text-gray-900">
            {photoCount} photo{photoCount === 1 ? '' : 's'}
          </p>
          <p className="text-xs text-gray-400 mt-0.5">Remove with the × on a photo below.</p>
        </div>
        <button
          onClick={handleAdd}
          disabled={busy}
          className="shrink-0 px-2.5 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-40"
        >
          {busy ? 'Adding…' : '+ Add photo'}
        </button>
      </div>
      {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
    </div>
  );
}

/** The × on a single gallery photo. */
function RemovePhotoButton({
  websiteId,
  activePage,
  sectionId,
  index,
  disabled,
  onContentChange,
}: {
  websiteId: string;
  activePage: string;
  sectionId: string;
  index: number;
  disabled: boolean;
  onContentChange: () => void;
}) {
  const [busy, setBusy] = useState(false);

  const handleRemove = useCallback(async () => {
    setBusy(true);
    try {
      const result = await applyStructuralChanges(websiteId, [
        {
          id: crypto.randomUUID(),
          action: 'remove_gallery_image',
          target: { page: activePage, section: sectionId, index },
          data: {},
        },
      ]);
      if (!result?.success) throw new Error(result?.error || 'Could not remove the photo.');
      onContentChange();
    } catch (err) {
      window.alert((err as Error).message);
      setBusy(false);
    }
  }, [websiteId, activePage, sectionId, index, onContentChange]);

  return (
    <button
      onClick={handleRemove}
      disabled={busy || disabled}
      title={disabled ? 'A gallery needs at least one photo' : 'Remove this photo'}
      className="shrink-0 w-6 h-6 flex items-center justify-center rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-400"
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  );
}

/** The same add action as the header, within reach at the end of a long list. */
function BottomAddPhotoButton({
  websiteId,
  activePage,
  sectionId,
  photoCount,
  onContentChange,
}: GalleryPhotoControlsProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAdd = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await addGalleryPhoto(websiteId, activePage, sectionId, photoCount);
      onContentChange();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }, [websiteId, activePage, sectionId, photoCount, onContentChange]);

  return (
    <div className="pt-1">
      <button
        onClick={handleAdd}
        disabled={busy}
        className="w-full px-3 py-2 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-md disabled:opacity-40"
      >
        {busy ? 'Adding…' : '+ Add photo'}
      </button>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

export function EditPanel({
  sectionId,
  sectionLabel,
  sectionType,
  elements,
  websiteId,
  activePage,
  sectionIndex,
  totalSections,
  pageSectionIds,
  mode = 'admin',
  allowGalleryPhotos = true,
  onChange,
  onPositionChange,
  onHrefChange,
  onClose,
  onContentChange,
}: EditPanelProps) {
  const label = SECTION_LABELS[sectionType] || sectionLabel;
  const showSectionActions = mode === 'admin' && websiteId && activePage != null && sectionIndex != null && totalSections != null && pageSectionIds && onContentChange;

  // Add/remove photos only on a gallery already converted to the data model
  // (ADR-009). On a frozen gallery these would produce changes the renderer
  // never applies, so the controls stay hidden rather than failing quietly.
  const galleryIndices = galleryImageIndices(sectionType, elements);
  const showGalleryPhotos =
    mode === 'admin' &&
    galleryIndices.length > 0 &&
    Boolean(websiteId) &&
    activePage != null &&
    Boolean(onContentChange) && allowGalleryPhotos;

  return (
    <div className="w-80 h-full bg-white border-l border-gray-200 flex flex-col shrink-0 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 truncate">{label} Section</h3>
          <p className="text-xs text-gray-400 mt-0.5">
            {elements.length} editable {elements.length === 1 ? 'element' : 'elements'}
          </p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {showSectionActions && (
            <SectionActions
              websiteId={websiteId}
              activePage={activePage}
              sectionId={sectionId}
              sectionType={sectionType}
              sectionIndex={sectionIndex}
              totalSections={totalSections}
              pageSectionIds={pageSectionIds}
              onContentChange={onContentChange}
            />
          )}
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
            title="Close panel"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Elements */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {showGalleryPhotos && (
          <GalleryPhotoControls
            websiteId={websiteId as string}
            activePage={activePage as string}
            sectionId={sectionId}
            photoCount={galleryIndices.length}
            onContentChange={onContentChange as () => void}
          />
        )}

        {elements.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">
            No editable elements in this section.
          </p>
        ) : (
          elements.map((element) => {
            const handleElementChange = (newValue: string) => {
              onChange(element.id, newValue);
            };
            const handleElementPosition = onPositionChange
              ? (position: string) => onPositionChange(element.id, position)
              : undefined;
            const handleElementHref = onHrefChange
              ? (href: string) => onHrefChange(element.id, href)
              : undefined;

            switch (element.type) {
              case 'text':
                return (
                  <TextEditor
                    key={element.id}
                    element={element}
                    onChange={handleElementChange}
                  />
                );
              case 'image': {
                const galleryMatch = showGalleryPhotos ? element.id.match(GALLERY_IMAGE_ID) : null;

                // A converted gallery keeps alt and caption in their own
                // elements beside the image. Hand them to the modal so it shows
                // and saves the same values as the fields below it.
                const sibling = (suffix: string) => {
                  if (!galleryMatch) return undefined;
                  const id = `${sectionId}_images_${galleryMatch[1]}_${suffix}`;
                  const el = elements.find((e) => e.id === id);
                  if (!el) return undefined;
                  return { value: String(el.value ?? ''), onChange: (v: string) => onChange(id, v) };
                };

                const editor = (
                  <ImageEditor
                    key={element.id}
                    element={element}
                    onChange={handleElementChange}
                    onPositionChange={handleElementPosition}
                    websiteId={websiteId}
                    text={galleryMatch ? { alt: sibling('alt'), caption: sibling('caption') } : undefined}
                  />
                );
                if (!galleryMatch) return editor;

                const photoPos = galleryIndices.indexOf(Number(galleryMatch[1]));
                return (
                  <div key={element.id} className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">{editor}</div>
                    <div className="flex flex-col gap-0.5">
                      <MovePhotoButton
                        websiteId={websiteId as string}
                        activePage={activePage as string}
                        sectionId={sectionId}
                        index={Number(galleryMatch[1])}
                        direction={-1}
                        disabled={photoPos <= 0}
                        onContentChange={onContentChange as () => void}
                      />
                      <MovePhotoButton
                        websiteId={websiteId as string}
                        activePage={activePage as string}
                        sectionId={sectionId}
                        index={Number(galleryMatch[1])}
                        direction={1}
                        disabled={photoPos < 0 || photoPos >= galleryIndices.length - 1}
                        onContentChange={onContentChange as () => void}
                      />
                    </div>
                    <RemovePhotoButton
                      websiteId={websiteId as string}
                      activePage={activePage as string}
                      sectionId={sectionId}
                      index={Number(galleryMatch[1])}
                      // A gallery with no images renders an empty placeholder
                      // slot anyway, so removing the last one gains nothing and
                      // loses the section's shape.
                      disabled={galleryIndices.length <= 1}
                      onContentChange={onContentChange as () => void}
                    />
                  </div>
                );
              }
              case 'cta':
                return (
                  <CtaEditor
                    key={element.id}
                    element={element}
                    onChange={handleElementChange}
                    onHrefChange={handleElementHref}
                  />
                );
              default:
                return null;
            }
          })
        )}

        {showGalleryPhotos && galleryIndices.length > 0 && (
          <BottomAddPhotoButton
            websiteId={websiteId as string}
            activePage={activePage as string}
            sectionId={sectionId}
            photoCount={galleryIndices.length}
            onContentChange={onContentChange as () => void}
          />
        )}
      </div>

      {/* Footer hint */}
      <div className="px-4 py-2.5 border-t border-gray-200 bg-gray-50">
        <p className="text-xs text-gray-400 text-center">
          Changes save automatically
        </p>
      </div>
    </div>
  );
}
